import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DotsVerticalIcon,
  DownloadIcon,
  ExternalLinkIcon,
  EyeOpenIcon,
  FileTextIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Menu, NativeFileInput, NativeInput, Text, UnstyledButton } from '@/shared/ui';
import { DocumentViewer } from '@/shared/ui/components/composed/document-viewer/DocumentViewer';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { formatLocalizedDate } from '@/shared/utils/date';
import { cn } from '@/shared/ui/utils/tailwind';
import type { PropertyDocument } from '../hooks/usePropertyDocuments';
import type { PropertyDocumentation } from '@services/housien/types-property';
import { collectDocumentationRows } from '@/shared/utils/property-detail-fields';
import { SectionCard, InfoRow } from '../SectionCard';
import { PropertyDialog } from '../PropertyDialog';

/** Longest display filename accepted when renaming. */
const FILENAME_MAX = 200;

interface DocumentsTabProps {
  documents: PropertyDocument[];
  loading: boolean;
  error: string | null;
  onUpload: (file: File) => void;
  onDelete: (documentId: string) => void;
  onRename: (documentId: string, filename: string) => void;
  documentation?: PropertyDocumentation;
}

/** One per-file action, rendered as a button on desktop and a menu row on touch. */
interface FileAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  run: () => void;
  destructive?: boolean;
  /** Only shown in the touch menu — the row itself carries it on desktop. */
  menuOnly?: boolean;
}

/**
 * Formats a byte count into a human-readable file size (B, KB or MB).
 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Opens the document in a new tab, using the link signed to render inline.
 *
 * @param doc - The document to open.
 */
function openDocumentTab(doc: PropertyDocument): void {
  const href = doc.view_url || doc.download_url;
  if (href) window.open(href, '_blank', 'noopener,noreferrer');
}

/**
 * Saves the document to disk, using the link signed as an attachment.
 *
 * @param doc - The document to download.
 */
function downloadDocument(doc: PropertyDocument): void {
  if (!doc.download_url) return;
  const anchor = document.createElement('a');
  anchor.href = doc.download_url;
  anchor.download = doc.filename;
  anchor.rel = 'noopener noreferrer';
  anchor.click();
}

/**
 * Registry metadata (matrícula / cartório / inscrição) above the file list.
 */
const RegistryCard: React.FC<{ documentation?: PropertyDocumentation }> = ({ documentation }) => {
  const { t } = useTranslation('features/properties');
  const rows = collectDocumentationRows(documentation);
  if (rows.length === 0) return null;
  return (
    <SectionCard
      title={t('detailView.documents.registryTitle')}
      data-testid="documents-registry-meta"
    >
      {rows.map((row) => (
        <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />
      ))}
    </SectionCard>
  );
};

/**
 * The per-file actions, as buttons on desktop and a menu on touch.
 *
 * Several 30px targets side by side are several ways to hit the wrong one
 * with a thumb — and one of them deletes. On touch they collapse into a
 * single menu, where each row is a full-width target, deleting is visibly
 * apart, and the viewer gets a row of its own instead of relying on a tap
 * on the file name.
 */
const FileActions: React.FC<{ actions: FileAction[]; menuLabel: string }> = ({
  actions,
  menuLabel,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Menu position="bottom-end" width={200}>
        <Menu.Target>
          <UnstyledButton
            type="button"
            aria-label={menuLabel}
            className="flex h-[36px] w-[36px] flex-none items-center justify-center rounded-(--radius-sm) border border-solid border-[var(--border-subtle)] bg-[var(--surface-primary)] text-[var(--text-secondary)]"
          >
            <DotsVerticalIcon width={16} height={16} />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown className="p-0">
          {actions.map((action) => (
            <Menu.Item
              key={action.id}
              onClick={action.run}
              className="items-center gap-(--space-2-5) rounded-none px-(--space-3) py-(--space-3)"
            >
              <Text
                component="span"
                className={cn(
                  'flex shrink-0',
                  action.destructive ? 'text-(--status-error-text)' : 'text-(--text-tertiary)',
                )}
              >
                {action.icon}
              </Text>
              <Text
                component="span"
                className={cn(
                  'text-[length:var(--text-body-sm)]',
                  action.destructive ? 'text-(--status-error-text)' : 'text-(--text-primary)',
                )}
              >
                {action.label}
              </Text>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <>
      {actions.filter((action) => !action.menuOnly).map((action) => (
        <UnstyledButton
          key={action.id}
          type="button"
          onClick={action.run}
          aria-label={action.label}
          title={action.label}
          className={cn(
            'flex h-[30px] w-[30px] flex-none items-center justify-center rounded-(--radius-sm) border border-solid border-[var(--border-subtle)] bg-[var(--surface-primary)] hover:bg-[var(--surface-hover)]',
            action.destructive ? 'text-[var(--status-error-text)]' : 'text-[var(--text-secondary)]',
          )}
        >
          {action.icon}
        </UnstyledButton>
      ))}
    </>
  );
};

/**
 * One uploaded file: icon, name, meta line and the per-file actions.
 */
const FileRow: React.FC<{
  doc: PropertyDocument;
  onView: () => void;
  onOpenInNewTab: () => void;
  onDownload: () => void;
  onRename: () => void;
  onDelete: () => void;
}> = ({ doc, onView, onOpenInNewTab, onDownload, onRename, onDelete }) => {
  const { t } = useTranslation('features/properties');
  const meta = [formatFileSize(doc.file_size), formatLocalizedDate(doc.upload_date)]
    .filter(Boolean)
    .join(' · ');

  /* Opening and saving are two separate controls: the one button this row
     used to have was labelled "open in a new tab" and downloaded instead,
     because the link it followed was signed as an attachment. */
  const actions: FileAction[] = [
    { id: 'view', label: t('detailView.documents.view'), icon: <EyeOpenIcon width={14} height={14} />, run: onView, menuOnly: true },
    { id: 'newTab', label: t('detailView.documents.openInNewTab'), icon: <ExternalLinkIcon width={14} height={14} />, run: onOpenInNewTab },
    { id: 'download', label: t('detailView.documents.download'), icon: <DownloadIcon width={14} height={14} />, run: onDownload },
    { id: 'rename', label: t('detailView.documents.rename'), icon: <Pencil1Icon width={14} height={14} />, run: onRename },
    { id: 'delete', label: t('detailView.documents.delete'), icon: <TrashIcon width={14} height={14} />, run: onDelete, destructive: true },
  ];

  return (
    <div
      data-testid="property-document-row"
      className="flex items-center gap-[var(--space-3)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] px-[var(--space-3)] py-[var(--space-2)]"
    >
      <Text component="span" className="flex flex-none text-[var(--text-tertiary)]">
        <FileTextIcon width={16} height={16} />
      </Text>
      <div className="min-w-0 flex-1">
        <UnstyledButton
          type="button"
          onClick={onView}
          title={t('detailView.documents.view')}
          className="block w-full truncate text-left text-[length:var(--text-body-sm)] text-[var(--text-primary)] hover:underline"
        >
          {doc.filename}
        </UnstyledButton>
        {meta && (
          <Text variant="meta" className="m-0 text-[var(--text-tertiary-soft)]">
            {meta}
          </Text>
        )}
      </div>
      <FileActions actions={actions} menuLabel={t('detailView.documents.actionsLabel')} />
    </div>
  );
};

/**
 * Renders legal registry fields plus the uploaded document files.
 *
 * @param props - Documents, load state and the file mutations.
 * @returns The rendered tab.
 */
export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  loading,
  error,
  onUpload,
  onDelete,
  onRename,
  documentation,
}) => {
  const { t } = useTranslation('features/properties');
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewing, setViewing] = useState<PropertyDocument | null>(null);
  const [renaming, setRenaming] = useState<PropertyDocument | null>(null);
  const [deleting, setDeleting] = useState<PropertyDocument | null>(null);
  const [draft, setDraft] = useState('');

  const startRename = (doc: PropertyDocument) => {
    setDraft(doc.filename);
    setRenaming(doc);
  };

  const confirmRename = () => {
    const next = draft.trim();
    if (renaming && next && next !== renaming.filename) onRename(renaming.document_id, next);
    setRenaming(null);
  };

  const confirmDelete = () => {
    if (deleting) onDelete(deleting.document_id);
    setDeleting(null);
  };

  const handlePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onUpload(file);
    event.target.value = '';
  };

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <RegistryCard documentation={documentation} />

      <SectionCard
        title={t('detailView.documents.title')}
        data-testid="property-documents-card"
        titleAccessory={
          <UnstyledButton
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="property-document-upload"
            className="h-[34px] whitespace-nowrap rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] px-[var(--space-3)] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]"
          >
            {t('detailView.documents.upload')}
          </UnstyledButton>
        }
      >
        <NativeFileInput ref={inputRef} className="hidden" onChange={handlePicked} />

        {loading ? (
          <div className="flex flex-col gap-[var(--space-2)]">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[52px] animate-pulse rounded-(--radius-md) bg-[var(--surface-tertiary)]" />
            ))}
          </div>
        ) : error ? (
          <Text variant="bodySm" className="m-0 text-[var(--status-error-text)]">
            {t('detailView.documents.loadError', { error })}
          </Text>
        ) : documents.length === 0 ? (
          <div className="flex h-[120px] items-center justify-center text-[length:var(--text-body-sm)] text-[var(--text-tertiary)]">
            {t('detailView.documents.empty')}
          </div>
        ) : (
          <div className="flex flex-col gap-[var(--space-2)]">
            {documents.map((doc) => (
              <FileRow
                key={doc.document_id}
                doc={doc}
                onView={() => setViewing(doc)}
                onOpenInNewTab={() => openDocumentTab(doc)}
                onDownload={() => downloadDocument(doc)}
                onRename={() => startRename(doc)}
                onDelete={() => setDeleting(doc)}
              />
            ))}
          </div>
        )}

        <Text variant="meta" className="mt-[var(--space-3)] m-0 text-[var(--text-tertiary-soft)]">
          {t('detailView.documents.uploadHint')}
        </Text>
      </SectionCard>

      <DocumentViewer<PropertyDocument>
        doc={viewing}
        onClose={() => setViewing(null)}
        i18nNamespace="features/properties"
        i18nKeyPrefix="detailView.documents"
      />

      {renaming && (
        <PropertyDialog
          title={t('detailView.documents.rename')}
          width="440px"
          confirmLabel={t('detailView.documents.renameConfirm')}
          onConfirm={confirmRename}
          confirmDisabled={draft.trim().length === 0}
          onClose={() => setRenaming(null)}
          data-testid="property-document-rename-dialog"
        >
          <NativeInput
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={FILENAME_MAX}
            autoFocus
            aria-label={t('detailView.documents.nameLabel')}
            className="h-[46px] w-full rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] px-[var(--space-3)] text-[length:var(--text-body-sm)] text-[var(--text-primary)] [font-family:var(--font-family)]"
          />
        </PropertyDialog>
      )}

      {deleting && (
        <PropertyDialog
          title={t('detailView.documents.delete')}
          width="440px"
          confirmLabel={t('detailView.documents.deleteConfirm')}
          onConfirm={confirmDelete}
          destructive
          onClose={() => setDeleting(null)}
          data-testid="property-document-delete-dialog"
        >
          <Text variant="bodySm" className="m-0 leading-[var(--leading-relaxed)] text-[var(--text-primary)]">
            {t('detailView.documents.deleteBody', { filename: deleting.filename })}
          </Text>
        </PropertyDialog>
      )}
    </div>
  );
};
